import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, useParams } from '@tanstack/react-router';
import { Button, Chip, Chips, Link, SystemIcon, Typography } from '@volvo/vcdk-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { IPublication } from '@/service/vpc/types/IPublication';
import type { ColumnDef } from '@tanstack/react-table';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import SelectMenu from '@/components/SelectMenu';
import Table from '@/components/Table';
import { loaderUnitPublicationsPage } from '@/loaders/unit.publications.loader';
import { getPublicationsQueryOptions, unitByIdQueryOptions } from '@/service/vpc/queryOptions';
import { vpcService } from '@/service/vpc/vpcService';
import { chassis } from '@/utils';

const PUBLICATION_CATEGORY_MAP: Record<string, string> = {
  'Operators manual': 'Operational',
  'Operators manual SOLAS': 'Operational',
  'Service and Maintenance': 'Service and maintenance',
  'Service Protocol': 'Service and maintenance',
  'Health Check Protocol Marine': 'Service and maintenance',
  'Service Book Marine': 'Service and maintenance',
  'Generic link to E-com': 'Service and maintenance',
  'DTC Shut Down Module': 'Professional Troubleshooting & Repair',
  Troubleshooting: 'Professional Troubleshooting & Repair',
  'DTC Telematics Gateway': 'Professional Troubleshooting & Repair',
  'Wiring diagram': 'Professional Troubleshooting & Repair',
  'Wiring diagram power supply': 'Professional Troubleshooting & Repair',
  'DTC EVC 2.0': 'Professional Troubleshooting & Repair',
  'Repair and specifications': 'Professional Troubleshooting & Repair',
  DTC: 'Professional Troubleshooting & Repair',
  'Design and Function': 'Professional Troubleshooting & Repair',
  'Accessory product leaflet': 'Other',
  'Product leaflet': 'Other',
  'Product Bulletin': 'Other',
  'Declaration of Conformity DB': 'Other',
};

const CATEGORY_ORDER = ['Operational', 'Service and maintenance', 'Professional Troubleshooting & Repair', 'Other'];

const getPublicationCategory = (publicationTypeCode: string): string =>
  PUBLICATION_CATEGORY_MAP[publicationTypeCode] ?? 'Other';

export function UnitPublicationsPage() {
  const { t } = useTranslation();
  const { endCustomerUnitId } = useParams({
    strict: false,
  });

  // Detect browser language once
  const [browserLanguage] = useState(() => {
    const navLang = navigator.language;
    return navLang;
  });

  const [selectedFilters, setSelectedFilters] = useState<{
    category: string[];
    designation: string[];
    productType: string[];
    language: string[];
  }>({
    category: [],
    designation: [],
    productType: [],
    language: ['Default'],
  });

  const LanguageTranslations: { [key: string]: string } = {
    'ar-SA': 'Arabic (ar-SA)',
    'bg-BG': 'Bulgarian (bg-BG)',
    'zh-hans': 'Chinese (zh-hans)',
    'hr-HR': 'Croatian (hr-HR)',
    'da-DK': 'Danish (da-DK)',
    'nl-NL': 'Dutch (nl-NL)',
    'en-US': 'English (en-US)',
    'fi-FI': 'Finnish (fi-FI)',
    'fr-FR': 'French (fr-FR)',
    'de-DE': 'German (de-DE)',
    'el-GR': 'Greek (el-GR)',
    'it-IT': 'Italian (it-IT)',
    'ja-JP': 'Japanese (ja-JP)',
    'lv-LV': 'Latvian (lv-LV)',
    'lt-LT': 'Lithuanian (lt-LT)',
    'pl-PL': 'Polish (pl-PL)',
    'pt-PT': 'Portuguese (pt-PT)',
    'pt-BR': 'Portuguese (pt-BR)',
    'ru-RU': 'Russian (ru-RU)',
    'sk-SK': 'Slovak (sk-SK)',
    'es-ES': 'Spanish (es-ES)',
    'sv-SE': 'Swedish (sv-SE)',
    'tr-TR': 'Turkish (tr-TR)',
  };

  const { data: unit } = useQuery(unitByIdQueryOptions({ endCustomerUnitId: endCustomerUnitId! }));
  const serialNumbers = chassis.getUnitSerialNumbers(unit) || [];

  const { data, isLoading } = useQuery(
    getPublicationsQueryOptions({
      endCustomerUnitId: endCustomerUnitId!,
      serialNumbers: serialNumbers.length > 0 ? serialNumbers : undefined,
    })
  );

  const { mutateAsync: fetchMetaData } = useMutation({
    mutationFn: ({ metadataUrl }: { metadataUrl: string }) =>
      vpcService.Publications.getPublicationMetadata({ metadataUrl }),
  });

  const { mutateAsync: fetchBinary } = useMutation({
    mutationFn: ({ binaryUrl }: { binaryUrl: string }) => vpcService.Publications.getPublicationBinary({ binaryUrl }),
  });

  const handleDownloadPublication = async (publication: IPublication) => {
    const metadataUrl = publication.metadataUrl;
    if (metadataUrl) {
      const metadata = await fetchMetaData({ metadataUrl });
      const binaryUrl = publication.url;
      if (binaryUrl) {
        const { blob, contentType, fileName: responseFileName } = await fetchBinary({ binaryUrl });

        const metadataFilename = metadata?.publicationVersions?.[0]?.binaryProperties?.filename;
        const metadataFileExtension = metadata?.publicationVersions?.[0]?.binaryProperties?.fileExtension;

        const downloadBlob = new Blob([blob], { type: contentType });
        let fileExtension = metadataFileExtension || '.pdf';

        if (!metadataFileExtension) {
          if (contentType.includes('pdf')) fileExtension = '.pdf';
          else if (contentType.includes('xml')) fileExtension = '.xml';
          else if (contentType.includes('zip')) fileExtension = '.zip';
          else if (contentType.includes('doc')) fileExtension = '.doc';
        }
        const finalFileName = metadataFilename || responseFileName || `${publication.title}${fileExtension}`;
        const href = window.URL.createObjectURL(downloadBlob);
        const link = document.createElement('a');
        link.href = href;
        link.setAttribute('download', finalFileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(href);
      }
    }
  };

  const columns = [
    {
      accessorFn: (row) => row.title,
      id: 'title',
      header: t(($) => $.components.table.publications_table.title),
      cell: (info) => {
        const row = info.row.original;
        if (!row.url) {
          return (
            <Typography variant="caption1" className="text-subtle">
              {info.getValue<string>()}
            </Typography>
          );
        }
        return (
          <Link variant="inline-discreet" className="text-subtle" onClick={() => handleDownloadPublication(row)}>
            {info.getValue<string>()}
          </Link>
        );
      },
    },
    {
      accessorFn: (row) => row.descriptionText,
      id: 'description',
      header: t(($) => $.components.table.publications_table.description),
      cell: (info) => {
        return (
          <Typography variant="caption1" className="text-subtle">
            {info.getValue<string>() ?? '-'}
          </Typography>
        );
      },
    },
    {
      accessorFn: (row) => row.informationArea,
      id: 'designation',
      header: t(($) => $.components.table.publications_table.designation),
      cell: (info) => {
        return (
          <Typography variant="caption1" className="text-subtle">
            {info.getValue<string>() ?? '-'}
          </Typography>
        );
      },
    },
    {
      accessorFn: (row) => row.productType,
      id: 'product',
      header: t(($) => $.components.table.publications_table.product),
      cell: (info) => {
        return (
          <Typography variant="caption1" className="text-subtle whitespace-nowrap">
            {info.getValue<string>() ?? '-'}
          </Typography>
        );
      },
    },
    {
      accessorFn: (row) => row.formatSize,
      id: 'formatSize',
      header: t(($) => $.components.table.publications_table.size),
      cell: (info) => {
        return (
          <Typography variant="caption1" className="text-subtle">
            {info.getValue<string>() ?? '-'}
          </Typography>
        );
      },
    },
    {
      accessorFn: (row) => row.languageCode,
      id: 'languageCode',
      header: t(($) => $.components.table.publications_table.languageCode),
      cell: (info) => {
        const langCode = info.getValue<string>();
        return (
          <Typography variant="caption1" className="text-subtle">
            {LanguageTranslations[langCode] || langCode}
          </Typography>
        );
      },
    },
    {
      accessorFn: (row) => row.buyableUrl,
      id: 'buyableUrl',
      header: t(($) => $.components.table.publications_table.buy),
      cell: (info) => {
        const buyableUrl = info.getValue<string | undefined>();
        if (!buyableUrl) return null;
        return (
          <a
            href={buyableUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex w-full items-center justify-start"
          >
            <SystemIcon icon="shopping-cart" size={20} />
          </a>
        );
      },
    },
  ] as ColumnDef<IPublication>[];

  const handleSetSelectedFilters = (
    filterType: 'category' | 'designation' | 'productType' | 'language',
    values: string[]
  ) => {
    if (filterType === 'language') {
      // If user selects other languages, remove "Default"
      const hasNonDefaultLanguages = values.some((v) => v !== 'Default');
      let newLanguages = values;

      if (hasNonDefaultLanguages) {
        // Remove "Default" if other languages are selected
        newLanguages = values.filter((v) => v !== 'Default');
      } else if (values.length === 0) {
        // If all languages unchecked, add "Default" back
        newLanguages = ['Default'];
      }

      setSelectedFilters((prev) => ({
        ...prev,
        language: newLanguages,
      }));
    } else {
      setSelectedFilters((prev) => ({
        ...prev,
        [filterType]: values,
      }));
    }
  };

  // create a list of all languages available in the publications
  const allLanguages = new Set<string>();
  data?.publications.forEach((publicationGroup) => {
    publicationGroup.forEach((publication) => {
      if (publication.languageCode) {
        allLanguages.add(publication.languageCode);
      }
    });
  });

  // Check if the browser language matches any available publication language
  const browserBase = browserLanguage.split('-')[0].toLowerCase();
  const hasBrowserLanguageMatch = Array.from(allLanguages).some((lang) => {
    const langBase = lang.split('-')[0].toLowerCase();
    return lang.toLowerCase() === browserLanguage.toLowerCase() || langBase === browserBase;
  });

  // Resolve the default language: use browser language if it matches, otherwise first available
  const resolvedDefaultLanguage = hasBrowserLanguageMatch
    ? browserLanguage
    : (Array.from(allLanguages)[0] ?? browserLanguage);

  // create a list of all Product types available in the publications
  const allProductTypes = new Set<string>();
  data?.publications.forEach((publicationGroup) => {
    publicationGroup.forEach((publication) => {
      if (publication.productType) {
        allProductTypes.add(publication.productType);
      }
    });
  });

  // create list of all Designations available in the publications
  const allDesignations = new Set<string>();
  data?.publications.forEach((publicationGroup) => {
    publicationGroup.forEach((publication) => {
      if (publication.informationArea) {
        allDesignations.add(publication.informationArea);
      }
    });
  });

  // Collect all available categories based on the publications data
  const allCategories = new Set<string>();
  data?.publications.forEach((publicationGroup) => {
    publicationGroup.forEach((publication) => {
      if (publication.publicationTypeCode) {
        allCategories.add(getPublicationCategory(publication.publicationTypeCode));
      }
    });
  });

  // filter publications based on selected filters
  const filteredPublications = data?.publications.flatMap((publicationGroup) =>
    publicationGroup.filter((publication) => {
      const categoryMatch =
        selectedFilters.category.length === 0 ||
        (publication.publicationTypeCode &&
          selectedFilters.category.includes(getPublicationCategory(publication.publicationTypeCode)));
      const designationMatch =
        selectedFilters.designation.length === 0 ||
        (publication.informationArea && selectedFilters.designation.includes(publication.informationArea));
      const productTypeMatch =
        selectedFilters.productType.length === 0 ||
        (publication.productType && selectedFilters.productType.includes(publication.productType));
      const languageMatch =
        selectedFilters.language.length === 0 ||
        selectedFilters.language.some((selectedLang) => {
          if (!publication.languageCode) return false;

          // If "Default" is selected, use resolved default language (browser language with fallback)
          const langToMatch = selectedLang === 'Default' ? resolvedDefaultLanguage : selectedLang;

          // Extract base language code (e.g., 'en' from 'en-US')
          const baseLang = publication.languageCode.split('-')[0].toLowerCase();
          const selectedBaseLang = langToMatch.split('-')[0].toLowerCase();

          // Match if either exact match or base language matches
          return publication.languageCode.toLowerCase() === langToMatch.toLowerCase() || baseLang === selectedBaseLang;
        });
      return categoryMatch && designationMatch && productTypeMatch && languageMatch;
    })
  );

  // Group filtered publications by the new category and sort by CATEGORY_ORDER
  const groupedByCategory = CATEGORY_ORDER.map((category) => ({
    category,
    publications: (filteredPublications ?? []).filter(
      (pub) => getPublicationCategory(pub.publicationTypeCode) === category
    ),
  })).filter((group) => group.publications.length > 0);

  return (
    <>
      <div className="mb-6 flex flex-row flex-wrap items-center gap-4">
        <SelectMenu
          label={t(($) => $.pages.unit.publications.select_menu.category)}
          options={Array.from(allCategories).map((category) => ({
            text: category,
            value: category,
          }))}
          selectedItems={selectedFilters.category}
          onChange={(values) => handleSetSelectedFilters('category', values)}
        />
        <SelectMenu
          label={t(($) => $.pages.unit.publications.select_menu.designation)}
          options={Array.from(allDesignations).map((designation) => ({
            text: designation,
            value: designation,
          }))}
          selectedItems={selectedFilters.designation}
          onChange={(values) => handleSetSelectedFilters('designation', values)}
        />
        <SelectMenu
          label={t(($) => $.pages.unit.publications.select_menu.product_type)}
          options={Array.from(allProductTypes).map((productType) => ({
            text: productType,
            value: productType,
          }))}
          selectedItems={selectedFilters.productType}
          onChange={(values) => handleSetSelectedFilters('productType', values)}
        />
        <SelectMenu
          label={t(($) => $.pages.unit.publications.select_menu.language)}
          options={[
            ...(hasBrowserLanguageMatch ? [{ text: 'Default (Browser Language)', value: 'Default' }] : []),
            ...Array.from(allLanguages).map((lang) => ({
              text: LanguageTranslations[lang] || lang,
              value: lang,
            })),
          ]}
          selectedItems={selectedFilters.language}
          onChange={(values) => handleSetSelectedFilters('language', values)}
          hideCountForValues={['Default']}
        />
      </div>
      <div className="mb-6 flex flex-row flex-nowrap items-center gap-4 overflow-auto">
        <Chips>
          {Object.entries(selectedFilters).map(([filterType, values]) =>
            values
              .filter((value) => !(filterType === 'language' && value === 'Default'))
              .map((value) => (
                <Chip
                  key={`${filterType}-${value}`}
                  removable
                  onRemove={() =>
                    handleSetSelectedFilters(
                      filterType as 'category' | 'designation' | 'productType' | 'language',
                      selectedFilters[filterType as keyof typeof selectedFilters].filter((v) => v !== value)
                    )
                  }
                >
                  {filterType === 'language' ? LanguageTranslations[value] || value : value}
                </Chip>
              ))
          )}
        </Chips>
        {selectedFilters.category.length +
          selectedFilters.designation.length +
          selectedFilters.productType.length +
          selectedFilters.language.filter((lang) => lang !== 'Default').length >
        0 ? (
          <Button
            onClick={() => {
              handleSetSelectedFilters('category', []);
              handleSetSelectedFilters('designation', []);
              handleSetSelectedFilters('productType', []);
              handleSetSelectedFilters('language', []);
            }}
            variant="tertiary"
          >
            Clear all
          </Button>
        ) : null}
      </div>
      {groupedByCategory.map((group) => (
        <div key={group.category} className="mb-8">
          <Table
            key={`publication-${group.category}-${group.publications.length}`}
            headerTitle={group.category}
            isLoading={isLoading}
            data={group.publications}
            columns={columns}
            showHeader={true}
            showSearch={true}
            showPagination={group.publications.length > 7}
          />
        </div>
      ))}
    </>
  );
}

export const Route = createFileRoute('/_authenticated/_account/fleets/$fleetId/units/$endCustomerUnitId/publications/')(
  {
    component: UnitPublicationsPage,
    errorComponent: ErrorBoundary,
    loader: loaderUnitPublicationsPage,
  }
);
